import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { GraduationCap, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiBase, formatApiFetchError } from '@/lib/apiBase';
import { setFlowOverride, setSessionToken, setSessionId } from '@/lib/flowStorage';
import { readStoredUtm } from '@/lib/utmCapture';
import { trackEvent } from '@/lib/trackEvent';
import PageSeo from '@/seo/PageSeo';
import BrandFormChrome from '@/components/BrandFormChrome';
import { BRAND_NAME } from '@/lib/brandConstants';
import {
  brandFormAccentBar,
  brandFormAmbient,
  brandFormCard,
  brandFormFooterBand,
  brandFormHeaderBand,
  brandFormInput,
  brandFormLabelInline,
  brandFormMesh,
  brandFormPage,
  brandFormPrimaryCta,
  brandFormSectionKicker,
} from '@/lib/brandFormShell';

export default function SchoolEntryPage() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [classGrade, setClassGrade] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [schoolLabel, setSchoolLabel] = useState('');
  const [slugErr, setSlugErr] = useState('');
  const [slugBusy, setSlugBusy] = useState(true);
  const formStartedRef = useRef(false);
  const onSchoolFormInteract = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    void trackEvent('form_started', {
      meta: { surface: 'school_entry', org: String(schoolId || '').trim().toLowerCase() || undefined },
    });
  };

  useEffect(() => {
    let cancelled = false;
    const slug = String(schoolId || '')
      .trim()
      .toLowerCase();
    if (!slug) {
      setSlugErr('Invalid school link.');
      setSlugBusy(false);
      return;
    }
    (async () => {
      setSlugBusy(true);
      setSlugErr('');
      try {
        const url = `${getApiBase()}/api/schools/${encodeURIComponent(slug)}/public`;
        let res;
        try {
          res = await fetch(url);
        } catch (e) {
          throw new Error(formatApiFetchError(url, e));
        }
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'School not found.');
        const label = String(data.name || '').trim() || slug;
        if (!cancelled) {
          setSchoolLabel(label);
          try {
            sessionStorage.setItem('crc_school_brand', JSON.stringify({ name: label, slug: data.slug || slug }));
          } catch {
            /* ignore */
          }
        }
      } catch (e) {
        if (!cancelled) setSlugErr(String(e.message || e));
      } finally {
        if (!cancelled) setSlugBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const slug = String(schoolId || '').trim().toLowerCase();
      if (!slug) throw new Error('Invalid school link.');
      const url = `${getApiBase()}/api/schools/${encodeURIComponent(slug)}/start`;
      let res;
      try {
        res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(),
            class: classGrade.trim(),
            utm: readStoredUtm(),
          }),
        });
      } catch (e) {
        throw new Error(formatApiFetchError(url, e));
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not start (${res.status})`);
      const sid = data.sessionId;
      const tok = data.sessionToken;
      if (!sid || !tok) throw new Error('Invalid server response.');
      setSessionId(sid);
      setSessionToken(tok);
      setFlowOverride('school');
      if (data.organization?.name) {
        try {
          sessionStorage.setItem(
            'crc_school_brand',
            JSON.stringify({
              name: String(data.organization.name).trim(),
              slug: String(data.organization.slug || slug).trim(),
            }),
          );
        } catch {
          /* ignore */
        }
      }
      /* `form_submitted` is recorded server-side in POST /api/schools/:slug/start (logFunnelEvent lead_created). */
      navigate('/assessment?flow=school');
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  const displaySchool = schoolLabel || String(schoolId || '').replace(/-/g, ' ');
  const pageTitle = schoolLabel ? `${schoolLabel} · assessment` : 'School assessment';

  return (
    <div className={brandFormPage}>
      <PageSeo
        title={pageTitle}
        description={`Start your career assessment${schoolLabel ? ` with ${schoolLabel}` : ''}. Powered by ${BRAND_NAME}.`}
        path={`/school/${schoolId || ''}`}
      />
      <div className={brandFormMesh} aria-hidden />
      <div className={brandFormAmbient} aria-hidden />
      <BrandFormChrome
        variant="school"
        wordmarkTitle={displaySchool}
        wordmarkSubtitle={`Powered by ${BRAND_NAME}`}
      />
      <div className="relative z-10 mx-auto max-w-md scroll-mt-28 pt-0 sm:pt-0">
        <div className={brandFormCard}>
          <div className={brandFormAccentBar} aria-hidden />
          <div className={`${brandFormHeaderBand} text-center sm:text-left`}>
            <div className="inline-flex items-center gap-2 rounded-full border border-teal-200/80 bg-white/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-teal-900 shadow-sm">
              <GraduationCap className="h-3.5 w-3.5 text-teal-600" aria-hidden />
              School program
            </div>
            <h1 className="mt-3 font-display text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
              {schoolLabel ? `Welcome · ${schoolLabel}` : 'School assessment'}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Enter your name and class to begin. No payment is required at this step.
            </p>
          </div>
          {slugBusy ? (
            <div className="flex flex-col items-center justify-center gap-3 px-5 py-14">
              <Loader2 className="h-8 w-8 animate-spin text-sky-600" aria-hidden />
              <p className="text-sm text-slate-600">Loading school…</p>
            </div>
          ) : slugErr ? (
            <p className="px-5 py-8 text-center text-sm text-amber-900">{slugErr}</p>
          ) : null}
          {!slugBusy && !slugErr ? (
          <form
            className="space-y-4 bg-gradient-to-b from-white to-teal-50/15 px-5 py-5 sm:px-6 sm:py-6"
            onSubmit={onSubmit}
            onFocusCapture={onSchoolFormInteract}
          >
            <p className={brandFormSectionKicker}>Your details</p>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className={brandFormLabelInline} htmlFor="sch-name">
                  Full name <span className="text-red-600">*</span>
                </label>
                <input
                  id="sch-name"
                  className={brandFormInput}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className={brandFormLabelInline} htmlFor="sch-class">
                  Class / grade <span className="text-red-600">*</span>
                </label>
                <input
                  id="sch-class"
                  className={brandFormInput}
                  value={classGrade}
                  onChange={(e) => setClassGrade(e.target.value)}
                  required
                />
              </div>
            </div>
            {err ? (
              <p className="rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-sm text-amber-900">{err}</p>
            ) : null}
            <Button type="submit" disabled={busy} className={brandFormPrimaryCta}>
              {busy ? (
                <>
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                  Starting…
                </>
              ) : (
                'Start assessment'
              )}
            </Button>
          </form>
          ) : null}
          <div className={brandFormFooterBand}>
            <Link to="/" className="text-sm font-semibold text-blue-700 underline-offset-2 hover:text-teal-800 hover:underline">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
